import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Subject } from 'rxjs/Subject';
import 'rxjs/add/operator/takeUntil';

import {
  SkillDocumentPreviewLinkResponse,
  SkillDocumentResponse,
} from '../../../models/skill-learning.model';
import { SkillSpecializationApiService } from '../../../services/skill-specialization-api.service';

const OFFICE_VIEWER_URL = 'https://view.officeapps.live.com/op/view.aspx?wdMobile=0&src=';
const OFFICE_VIEWER_MIN_WIDTH = 640;
const DEFAULT_VIEWER_SCALE = 1;
const VIEWER_HEIGHT_VH = 72;

@Component({
  selector: 'app-skill-document-preview-modal',
  templateUrl: './skill-document-preview-modal.component.html',
  styleUrls: ['./skill-document-preview-modal.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SkillDocumentPreviewModalComponent implements OnInit, OnDestroy {

  specializationId = '';
  level = 0;
  document: SkillDocumentResponse | null = null;

  viewerUrl: SafeResourceUrl | null = null;
  officeViewerUrl: string | null = null;
  viewerWidth: number | null = null;
  viewerScale = DEFAULT_VIEWER_SCALE;
  viewerHeightVh = VIEWER_HEIGHT_VH;
  isLoading = true;
  hasError = false;

  private objectUrl = '';
  private destroy$ = new Subject<void>();

  constructor(
    public activeModal: NgbActiveModal,
    private api: SkillSpecializationApiService,
    private sanitizer: DomSanitizer,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    if (!this.specializationId || !this.level || !this.document) {
      this.showError();
      return;
    }
    if (this.extension === 'pdf') {
      this.loadPdf();
      return;
    }
    if (this.extension === 'docx') {
      this.loadDocx();
      return;
    }
    this.showError();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.objectUrl) {
      window.URL.revokeObjectURL(this.objectUrl);
    }
  }

  get fileName(): string {
    return this.document && this.document.fileName
      ? this.document.fileName
      : 'document';
  }

  download(): void {
    const url = this.api.buildLevelDocumentDownloadUrl(this.specializationId, this.level);
    window.open(url, '_blank');
  }

  onViewerLoaded(width?: number): void {
    this.fitViewer(width);
    this.isLoading = false;
    this.cdr.markForCheck();
  }

  fitViewer(width: number): void {
    if (!this.officeViewerUrl || !width) {
      return;
    }
    this.viewerWidth = Math.max(width, OFFICE_VIEWER_MIN_WIDTH);
    this.viewerScale = width / this.viewerWidth;
    this.viewerHeightVh = VIEWER_HEIGHT_VH / this.viewerScale;
  }

  private get extension(): string {
    if (!this.document) {
      return '';
    }
    if (this.document.fileExtension) {
      return this.document.fileExtension.toLowerCase().replace(/^\./, '');
    }
    const fileName = this.document.fileName || '';
    const dotIndex = fileName.lastIndexOf('.');
    return dotIndex < 0 ? '' : fileName.substring(dotIndex + 1).toLowerCase();
  }

  private loadPdf(): void {
    this.api.downloadLevelDocumentBlob(this.specializationId, this.level)
      .takeUntil(this.destroy$)
      .subscribe(
        blob => {
          this.objectUrl = window.URL.createObjectURL(blob);
          this.viewerUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.objectUrl);
          this.cdr.markForCheck();
        },
        () => this.showError(),
      );
  }

  private loadDocx(): void {
    this.api.createLevelDocumentPreviewLink(this.specializationId, this.level)
      .takeUntil(this.destroy$)
      .subscribe(
        response => this.applyOfficeViewerUrl(response),
        () => this.showError(),
      );
  }

  private applyOfficeViewerUrl(response: SkillDocumentPreviewLinkResponse): void {
    const documentUrl = this.resolveOfficeDocumentUrl(response);
    if (!documentUrl) {
      this.showError();
      return;
    }
    this.officeViewerUrl = OFFICE_VIEWER_URL + encodeURIComponent(documentUrl);
    this.viewerUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.officeViewerUrl);
    this.cdr.markForCheck();
  }

  private resolveOfficeDocumentUrl(response: SkillDocumentPreviewLinkResponse): string {
    if (!response) {
      return '';
    }
    if (response.previewUrl) {
      return response.previewUrl;
    }
    if (!response.token) {
      return '';
    }
    const relativeUrl = this.api.buildDocumentPreviewUrl(
      response.token,
      response.fileName || this.fileName,
    );
    return window.location.protocol + '//' + window.location.host + relativeUrl;
  }

  private showError(): void {
    this.isLoading = false;
    this.hasError = true;
    this.cdr.markForCheck();
  }
}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/content-page/common/skill-learning/components/shared/skill-document-preview-modal/skill-document-preview-modal.component.ts